// src/pages/AdminCouponPage/AdminCouponPage.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert,
  Spinner,
  Badge,
  Table,
  Modal,
  Pagination,
  InputGroup
} from "react-bootstrap";
import { motion } from "framer-motion";
import { 
  FaTicketAlt, 
  FaPercentage, 
  FaRupeeSign, 
  FaCalendarAlt,
  FaInfoCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaGift,
  FaClock,
  FaSave,
  FaEdit,
  FaTrash,
  FaSearch,
  FaCopy
} from "react-icons/fa";
import Sidebar from "../../component/sidebar/sidebar";
import Header from "../../component/header/header";
import "./AdminCouponPage.css";

const AdminCouponPage = () => {
  const [formData, setFormData] = useState({
    code: "",
    discountType: "percentage",
    discountValue: "",
    minOrderAmount: "",
    maxDiscountAmount: "",
    expiryDate: "",
    description: "",
    isActive: true,
    usageLimit: "",
    perUserLimit: "",
  });

  const [coupons, setCoupons] = useState([]);
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCoupons, setLoadingCoupons] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [copiedCode, setCopiedCode] = useState(null);
  const itemsPerPage = 10;

  // ✅ FIX: Use the API URL without /api since it's already in the env
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:7000/api";

  // Fetch coupons on load
  useEffect(() => {
    fetchCoupons();
  }, []);

  // Apply filters and search
  useEffect(() => {
    let result = [...coupons];

    // Filter by status
    if (filterStatus !== "all") {
      const isActive = filterStatus === "active";
      result = result.filter(c => c.isActive === isActive);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(c => 
        c.code.toLowerCase().includes(term) ||
        c.description?.toLowerCase().includes(term) ||
        (c.discountType === "percentage" ? `${c.discountValue}%` : `₹${c.discountValue}`).includes(term)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === "discountValue") {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      } else if (sortBy === "createdAt" || sortBy === "expiryDate") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredCoupons(result);
    setCurrentPage(1);
  }, [coupons, searchTerm, filterStatus, sortBy, sortOrder]);

  const fetchCoupons = async () => {
    setLoadingCoupons(true);
    try {
      // ✅ FIX: Remove duplicate /api - use /coupons directly
      const res = await axios.get(`${API_URL}/coupons/admin/coupons`);
      setCoupons(res.data);
      setFilteredCoupons(res.data);
    } catch (error) {
      console.error("Error fetching coupons:", error);
      setError("Failed to load coupons");
    } finally {
      setLoadingCoupons(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    // Validation
    if (!formData.code.trim()) {
      setError("Coupon code is required");
      return;
    }
    
    if (!formData.discountValue || parseFloat(formData.discountValue) <= 0) {
      setError("Discount value must be greater than 0");
      return;
    }

    if (formData.discountType === "percentage" && parseFloat(formData.discountValue) > 100) {
      setError("Percentage discount cannot exceed 100%");
      return;
    }

    try {
      setLoading(true);

      // ✅ FIX: Remove duplicate /api
      const res = await axios.post(
        `${API_URL}/coupons/admin/coupon`,
        formData
      );

      setSuccess(true);
      await fetchCoupons(); // Refresh the list
      
      setTimeout(() => {
        setFormData({
          code: "",
          discountType: "percentage",
          discountValue: "",
          minOrderAmount: "",
          maxDiscountAmount: "",
          expiryDate: "",
          description: "",
          isActive: true,
          usageLimit: "",
          perUserLimit: "",
        });
        setSuccess(false);
      }, 3000);

    } catch (error) {
      console.error("Error creating coupon:", error);
      setError(error?.response?.data?.message || "Error creating coupon");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    
    try {
      // ✅ FIX: Remove duplicate /api
      await axios.delete(`${API_URL}/coupons/admin/coupon/${id}`);
      await fetchCoupons();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error deleting coupon:", error);
      setError("Failed to delete coupon");
    }
  };

  const handleEdit = (coupon) => {
    setEditCoupon(coupon);
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // ✅ FIX: Remove duplicate /api
      await axios.put(
        `${API_URL}/coupons/admin/coupon/${editCoupon._id}`,
        editCoupon
      );
      await fetchCoupons();
      setShowEditModal(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error updating coupon:", error);
      setError("Failed to update coupon");
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      // ✅ FIX: Remove duplicate /api
      await axios.patch(`${API_URL}/coupons/admin/coupon/${id}/toggle`, {
        isActive: !currentStatus
      });
      await fetchCoupons();
    } catch (error) {
      console.error("Error toggling coupon status:", error);
      setError("Failed to update coupon status");
    }
  };

  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const getStatusBadge = (isActive) => {
    return isActive ? 
      <Badge bg="success" className="px-3 py-2">Active</Badge> : 
      <Badge bg="secondary" className="px-3 py-2">Inactive</Badge>;
  };

  const getDiscountDisplay = (coupon) => {
    if (!coupon.discountValue) return "0% OFF";
    if (coupon.discountType === "percentage") {
      return `${coupon.discountValue}% OFF`;
    }
    return `₹${coupon.discountValue} OFF`;
  };

  const getDiscountDisplayForForm = () => {
    if (!formData.discountValue) return "0% OFF";
    if (formData.discountType === "percentage") {
      return `${formData.discountValue}% OFF`;
    }
    return `₹${formData.discountValue} OFF`;
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCoupons.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCoupons.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Format date
  const formatDate = (date) => {
    if (!date) return "Never";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  return (
    <div className="admin-coupon-page">
      <Header />
      <div className="admin-layout">
        <Sidebar />
        
        <main className="admin-content">
          <Container fluid className="py-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap">
                <div>
                  <h4 className="page-title mb-1">
                    <FaTicketAlt className="me-2 text-primary" />
                    Coupon Management
                  </h4>
                  <p className="text-muted">
                    Create and manage discount coupons for your customers
                  </p>
                </div>
                <Badge bg="info" pill className="px-3 py-2">
                  <FaGift className="me-1" />
                  {coupons.length} Coupons
                </Badge>
              </div>

              {/* Success Alert */}
              {success && (
                <Alert variant="success" className="d-flex align-items-center" onClose={() => setSuccess(false)} dismissible>
                  <FaCheckCircle className="me-2" size={20} />
                  <div>
                    <strong>Success!</strong> Operation completed successfully
                  </div>
                </Alert>
              )}

              {/* Error Alert */}
              {error && (
                <Alert variant="danger" className="d-flex align-items-center" onClose={() => setError("")} dismissible>
                  <FaTimesCircle className="me-2" size={20} />
                  <div>
                    <strong>Error!</strong> {error}
                  </div>
                </Alert>
              )}

              <Row>
                {/* Main Form */}
                <Col lg={8}>
                  <Card className="shadow-sm border-0 mb-4">
                    <Card.Body className="p-4">
                      <h5 className="fw-bold mb-3">
                        <FaGift className="me-2 text-primary" />
                        Create New Coupon
                      </h5>
                      <form onSubmit={handleSubmit}>
                        <Row>
                          {/* Coupon Code */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <FaTicketAlt className="me-1 text-primary" />
                                Coupon Code <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="code"
                                value={formData.code}
                                onChange={handleChange}
                                placeholder="e.g., SAVE10"
                                className="form-control-lg"
                                required
                                autoComplete="off"
                                style={{ textTransform: 'uppercase' }}
                              />
                              <Form.Text className="text-muted">
                                Use uppercase letters and numbers only
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          {/* Status */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <FaCheckCircle className="me-1 text-primary" />
                                Status
                              </Form.Label>
                              <Form.Select
                                name="isActive"
                                value={formData.isActive}
                                onChange={handleChange}
                                className="form-control-lg"
                              >
                                <option value={true}>Active</option>
                                <option value={false}>Inactive</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>

                          {/* Discount Type */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <FaPercentage className="me-1 text-primary" />
                                Discount Type <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Select
                                name="discountType"
                                value={formData.discountType}
                                onChange={handleChange}
                                className="form-control-lg"
                              >
                                <option value="percentage">Percentage (%)</option>
                                <option value="fixed">Fixed Amount (₹)</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>

                          {/* Discount Value */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <FaRupeeSign className="me-1 text-primary" />
                                Discount Value <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="number"
                                name="discountValue"
                                value={formData.discountValue}
                                onChange={handleChange}
                                placeholder={formData.discountType === "percentage" ? "e.g., 10" : "e.g., 100"}
                                className="form-control-lg"
                                min="0"
                                step="0.01"
                                required
                              />
                              <Form.Text className="text-muted">
                                {formData.discountType === "percentage" 
                                  ? "Enter percentage (e.g., 10 for 10%)" 
                                  : "Enter fixed amount in rupees"}
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          {/* Minimum Order Amount */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <FaRupeeSign className="me-1 text-primary" />
                                Minimum Order Amount
                              </Form.Label>
                              <Form.Control
                                type="number"
                                name="minOrderAmount"
                                value={formData.minOrderAmount}
                                onChange={handleChange}
                                placeholder="e.g., 500"
                                className="form-control-lg"
                                min="0"
                                step="1"
                              />
                              <Form.Text className="text-muted">
                                Minimum order value required to apply coupon
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          {/* Max Discount Amount */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <FaRupeeSign className="me-1 text-primary" />
                                Maximum Discount Amount
                              </Form.Label>
                              <Form.Control
                                type="number"
                                name="maxDiscountAmount"
                                value={formData.maxDiscountAmount}
                                onChange={handleChange}
                                placeholder="e.g., 500"
                                className="form-control-lg"
                                min="0"
                                step="1"
                              />
                              <Form.Text className="text-muted">
                                Maximum discount that can be applied (optional)
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          {/* Expiry Date */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <FaCalendarAlt className="me-1 text-primary" />
                                Expiry Date
                              </Form.Label>
                              <Form.Control
                                type="date"
                                name="expiryDate"
                                value={formData.expiryDate}
                                onChange={handleChange}
                                className="form-control-lg"
                              />
                            </Form.Group>
                          </Col>

                          {/* Usage Limit */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <FaClock className="me-1 text-primary" />
                                Usage Limit
                              </Form.Label>
                              <Form.Control
                                type="number"
                                name="usageLimit"
                                value={formData.usageLimit}
                                onChange={handleChange}
                                placeholder="Total uses allowed"
                                className="form-control-lg"
                                min="0"
                                step="1"
                              />
                              <Form.Text className="text-muted">
                                Maximum number of times this coupon can be used
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          {/* Per User Limit */}
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <FaInfoCircle className="me-1 text-primary" />
                                Per User Limit
                              </Form.Label>
                              <Form.Control
                                type="number"
                                name="perUserLimit"
                                value={formData.perUserLimit}
                                onChange={handleChange}
                                placeholder="Uses per user"
                                className="form-control-lg"
                                min="0"
                                step="1"
                              />
                              <Form.Text className="text-muted">
                                How many times each user can use this coupon
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          {/* Description */}
                          <Col xs={12}>
                            <Form.Group className="mb-3">
                              <Form.Label className="fw-semibold">
                                <FaInfoCircle className="me-1 text-primary" />
                                Description
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                rows="2"
                                placeholder="Describe the coupon details..."
                                className="form-control"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        {/* Submit Button */}
                        <div className="d-flex gap-3 mt-3">
                          <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            disabled={loading}
                            className="px-5"
                          >
                            {loading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  className="me-2"
                                />
                                Creating...
                              </>
                            ) : (
                              <>
                                <FaSave className="me-2" />
                                Create Coupon
                              </>
                            )}
                          </Button>
                          <Button
                            type="reset"
                            variant="outline-secondary"
                            size="lg"
                            onClick={() => {
                              setFormData({
                                code: "",
                                discountType: "percentage",
                                discountValue: "",
                                minOrderAmount: "",
                                maxDiscountAmount: "",
                                expiryDate: "",
                                description: "",
                                isActive: true,
                                usageLimit: "",
                                perUserLimit: "",
                              });
                              setError("");
                            }}
                          >
                            Reset
                          </Button>
                        </div>
                      </form>
                    </Card.Body>
                  </Card>

                  {/* Coupon List */}
                  <Card className="shadow-sm border-0">
                    <Card.Body className="p-4">
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                        <h5 className="fw-bold mb-0">
                          <FaTicketAlt className="me-2 text-primary" />
                          All Coupons
                          <Badge bg="secondary" className="ms-2">
                            {filteredCoupons.length}
                          </Badge>
                        </h5>
                        
                        <div className="d-flex gap-2 flex-wrap">
                          <InputGroup size="sm" style={{ width: '200px' }}>
                            <InputGroup.Text>
                              <FaSearch />
                            </InputGroup.Text>
                            <Form.Control
                              placeholder="Search coupons..."
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                          </InputGroup>
                          
                          <Form.Select 
                            size="sm" 
                            value={filterStatus} 
                            onChange={(e) => setFilterStatus(e.target.value)}
                            style={{ width: '130px' }}
                          >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Form.Select>
                        </div>
                      </div>

                      {loadingCoupons ? (
                        <div className="text-center py-4">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-2">Loading coupons...</p>
                        </div>
                      ) : filteredCoupons.length === 0 ? (
                        <Alert variant="info" className="text-center">
                          <FaGift size={30} className="mb-2" />
                          <p>{coupons.length === 0 ? "No coupons created yet" : "No coupons match your filters"}</p>
                        </Alert>
                      ) : (
                        <>
                          <div className="table-responsive">
                            <Table hover className="coupon-table">
                              <thead>
                                <tr>
                                  <th>Code</th>
                                  <th>Discount</th>
                                  <th>Min Order</th>
                                  <th>Status</th>
                                  <th>Expiry</th>
                                  <th>Uses</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {currentItems.map((coupon) => (
                                  <motion.tr
                                    key={coupon._id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    <td>
                                      <div className="coupon-code-display">
                                        <strong className="text-primary">{coupon.code}</strong>
                                        <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 ms-2"
                                          onClick={() => copyToClipboard(coupon.code)}
                                          title="Copy code"
                                        >
                                          <FaCopy />
                                        </Button>
                                        {copiedCode === coupon.code && (
                                          <Badge bg="success" className="ms-1">Copied!</Badge>
                                        )}
                                      </div>
                                    </td>
                                    <td>
                                      <span className="fw-bold">
                                        {getDiscountDisplay(coupon)}
                                      </span>
                                      <br />
                                      <small className="text-muted">
                                        {coupon.discountType === "percentage" ? "%" : "Fixed"}
                                      </small>
                                    </td>
                                    <td>
                                      {coupon.minOrderAmount ? `₹${coupon.minOrderAmount}` : "N/A"}
                                    </td>
                                    <td>
                                      <Button
                                        variant="link"
                                        className="p-0"
                                        onClick={() => handleToggleStatus(coupon._id, coupon.isActive)}
                                      >
                                        {getStatusBadge(coupon.isActive)}
                                      </Button>
                                    </td>
                                    <td>
                                      {coupon.expiryDate ? (
                                        <>
                                          <div>{formatDate(coupon.expiryDate)}</div>
                                          <small className={`text-${new Date(coupon.expiryDate) < new Date() ? 'danger' : 'success'}`}>
                                            {new Date(coupon.expiryDate) < new Date() ? 'Expired' : 'Active'}
                                          </small>
                                        </>
                                      ) : "Never"}
                                    </td>
                                    <td>
                                      <div>{coupon.usageCount || 0} used</div>
                                      {coupon.usageLimit && (
                                        <small className="text-muted">
                                          / {coupon.usageLimit} total
                                        </small>
                                      )}
                                    </td>
                                    <td>
                                      <div className="d-flex gap-1">
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() => handleEdit(coupon)}
                                          title="Edit"
                                        >
                                          <FaEdit />
                                        </Button>
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          onClick={() => handleDelete(coupon._id)}
                                          title="Delete"
                                        >
                                          <FaTrash />
                                        </Button>
                                      </div>
                                    </td>
                                  </motion.tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>

                          {/* Pagination */}
                          {totalPages > 1 && (
                            <div className="d-flex justify-content-center mt-3">
                              <Pagination>
                                <Pagination.Prev 
                                  onClick={() => paginate(currentPage - 1)}
                                  disabled={currentPage === 1}
                                />
                                {[...Array(totalPages)].map((_, i) => (
                                  <Pagination.Item
                                    key={i + 1}
                                    active={currentPage === i + 1}
                                    onClick={() => paginate(i + 1)}
                                  >
                                    {i + 1}
                                  </Pagination.Item>
                                ))}
                                <Pagination.Next
                                  onClick={() => paginate(currentPage + 1)}
                                  disabled={currentPage === totalPages}
                                />
                              </Pagination>
                            </div>
                          )}
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                {/* Preview Section */}
                <Col lg={4}>
                  <Card className="shadow-sm border-0 sticky-top" style={{ top: '20px' }}>
                    <Card.Body className="p-4">
                      <h6 className="fw-bold mb-3">
                        <FaGift className="me-2 text-primary" />
                        Coupon Preview
                      </h6>

                      <motion.div
                        className="coupon-preview"
                        initial={{ scale: 0.95 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="coupon-card">
                          <div className="coupon-header">
                            <Badge 
                              bg={formData.isActive ? "success" : "secondary"}
                              className="mb-2"
                            >
                              {formData.isActive ? "Active" : "Inactive"}
                            </Badge>
                            <h3 className="coupon-code">
                              {formData.code || "COUPON"}
                            </h3>
                          </div>

                          <div className="coupon-body">
                            <div className="discount-amount">
                              <span className="discount-value">
                                {getDiscountDisplayForForm()}
                              </span>
                              {formData.discountType === "percentage" && (
                                <span className="discount-type">Percentage</span>
                              )}
                            </div>

                            {formData.minOrderAmount && (
                              <div className="coupon-condition">
                                <FaRupeeSign className="me-1" />
                                Min Order: ₹{formData.minOrderAmount}
                              </div>
                            )}

                            {formData.maxDiscountAmount && (
                              <div className="coupon-condition">
                                <FaRupeeSign className="me-1" />
                                Max Discount: ₹{formData.maxDiscountAmount}
                              </div>
                            )}

                            {formData.expiryDate && (
                              <div className="coupon-condition">
                                <FaCalendarAlt className="me-1" />
                                Expires: {new Date(formData.expiryDate).toLocaleDateString()}
                              </div>
                            )}

                            {formData.description && (
                              <div className="coupon-description">
                                <FaInfoCircle className="me-1" />
                                {formData.description}
                              </div>
                            )}

                            <div className="coupon-footer">
                              <div className="coupon-usage">
                                {formData.usageLimit && (
                                  <span>Total: {formData.usageLimit} uses</span>
                                )}
                                {formData.perUserLimit && (
                                  <span className="ms-2">
                                    Per user: {formData.perUserLimit} uses
                                  </span>
                                )}
                              </div>
                              <div className="coupon-cta">
                                <Badge bg="primary">
                                  Copy Code
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>

                      {/* Quick Stats */}
                      <div className="mt-4">
                        <h6 className="fw-bold mb-2">Coupon Summary</h6>
                        <div className="coupon-stats">
                          <div className="stat-item">
                            <span className="stat-label">Status</span>
                            <Badge bg={formData.isActive ? "success" : "secondary"}>
                              {formData.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Discount</span>
                            <span className="stat-value">{getDiscountDisplayForForm()}</span>
                          </div>
                          {formData.minOrderAmount && (
                            <div className="stat-item">
                              <span className="stat-label">Min Order</span>
                              <span className="stat-value">₹{formData.minOrderAmount}</span>
                            </div>
                          )}
                          {formData.expiryDate && (
                            <div className="stat-item">
                              <span className="stat-label">Expiry</span>
                              <span className="stat-value">
                                {new Date(formData.expiryDate).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Tips */}
                      <Alert variant="info" className="mt-3">
                        <FaInfoCircle className="me-2" />
                        <small>
                          <strong>Tip:</strong> Create unique coupon codes that are 
                          easy to remember and share with customers.
                        </small>
                      </Alert>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </motion.div>
          </Container>
        </main>
      </div>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEdit className="me-2 text-primary" />
            Edit Coupon
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editCoupon && (
            <Form onSubmit={handleUpdate}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Coupon Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={editCoupon.code}
                      onChange={(e) => setEditCoupon({...editCoupon, code: e.target.value.toUpperCase()})}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={editCoupon.isActive}
                      onChange={(e) => setEditCoupon({...editCoupon, isActive: e.target.value === 'true'})}
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Discount Type</Form.Label>
                    <Form.Select
                      value={editCoupon.discountType}
                      onChange={(e) => setEditCoupon({...editCoupon, discountType: e.target.value})}
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Discount Value</Form.Label>
                    <Form.Control
                      type="number"
                      value={editCoupon.discountValue}
                      onChange={(e) => setEditCoupon({...editCoupon, discountValue: e.target.value})}
                      required
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Min Order Amount</Form.Label>
                    <Form.Control
                      type="number"
                      value={editCoupon.minOrderAmount}
                      onChange={(e) => setEditCoupon({...editCoupon, minOrderAmount: e.target.value})}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Max Discount Amount</Form.Label>
                    <Form.Control
                      type="number"
                      value={editCoupon.maxDiscountAmount}
                      onChange={(e) => setEditCoupon({...editCoupon, maxDiscountAmount: e.target.value})}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Expiry Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={editCoupon.expiryDate?.split('T')[0] || ''}
                      onChange={(e) => setEditCoupon({...editCoupon, expiryDate: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Usage Limit</Form.Label>
                    <Form.Control
                      type="number"
                      value={editCoupon.usageLimit}
                      onChange={(e) => setEditCoupon({...editCoupon, usageLimit: e.target.value})}
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows="2"
                      value={editCoupon.description}
                      onChange={(e) => setEditCoupon({...editCoupon, description: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex gap-2">
                <Button type="submit" variant="primary">
                  <FaSave className="me-2" />
                  Update Coupon
                </Button>
                <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminCouponPage;